<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, watch } from "vue";

/**
 * A barra fina do alto da página — o "há trabalho em voo" do produto inteiro.
 *
 * Primitiva, e por isso cega: recebe o `state` pronto e só desenha. QUEM está em
 * voo (pedido, navegação, abertura de stream, upload) é contado em
 * `shared/http/loading-bar`, e é o shell que liga os dois — `design/` não conhece
 * a rede, e a regra do depcruise diz o mesmo.
 *
 * O desenho é o do nprogress, que é o que a V2 tinha (via naive-ui) e o que
 * qualquer pessoa já leu mil vezes:
 * - `loading`: salta para 8% e avança sozinha, rápido no começo e cada vez mais
 *   devagar, sem nunca chegar ao fim — ninguém sabe quanto falta, e uma barra
 *   que para em 100% à espera mente mais do que uma que desacelera.
 * - `idle` depois de carregar: corre até 100% e some num fade.
 * - `error`: corre até 100% em vermelho e fica, até o estado voltar a `idle`
 *   (quem decide quanto tempo é o contador, ~1,2 s) — aí some como a azul.
 *
 * O avanço é um `setTimeout` encadeado que só existe enquanto `loading`; parado,
 * não há relógio nenhum. Com `prefers-reduced-motion` não há avanço nem
 * transição: a barra aparece inteira e some.
 *
 * `aria-hidden`: é decorativa. Quem fala com o leitor de tela é o `Toast` e o
 * estado de cada botão; uma região viva a anunciar cada pedido seria ruído.
 */
const props = defineProps<{ state: "idle" | "loading" | "error" }>();

/** 0–1. Nunca passa de ~0,994 enquanto carrega; 1 só no fim. */
const progress = ref(0);
const shown = ref(false);
const tone = ref<"accent" | "danger">("accent");
/**
 * Sem transição no REINÍCIO: voltar de 100% para 0% animado seria ver a barra
 * correr para trás antes de começar.
 */
const animate = ref(false);

let trickleTimer: ReturnType<typeof setTimeout> | undefined;
let hideTimer: ReturnType<typeof setTimeout> | undefined;
let resetTimer: ReturnType<typeof setTimeout> | undefined;

function reducedMotion(): boolean {
	return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
}

/** Os degraus do nprogress: rápido até 20%, depois cada vez menos. */
function step(p: number): number {
	if (p < 0.2) return 0.1;
	if (p < 0.5) return 0.04;
	if (p < 0.8) return 0.02;
	if (p < 0.994) return 0.005;
	return 0;
}

function trickle(): void {
	trickleTimer = setTimeout(() => {
		progress.value = Math.min(progress.value + step(progress.value), 0.994);
		trickle();
	}, 250);
}

function clearAll(): void {
	clearTimeout(trickleTimer);
	clearTimeout(hideTimer);
	clearTimeout(resetTimer);
}

async function start(): Promise<void> {
	tone.value = "accent";
	const reduced = reducedMotion();
	// Vinda do repouso ou de um fim ainda em fade: recomeça do zero, sem animar a
	// volta. Vinda do vermelho a 100%, idem — é uma rodada nova.
	if (!shown.value || progress.value >= 1) {
		animate.value = false;
		progress.value = 0;
		shown.value = true;
		await nextTick();
		// Um frame para o navegador pintar o 0% antes de ligar a transição; sem
		// ele, o salto para 8% sai sem animação nenhuma.
		await new Promise((resolve) => requestAnimationFrame(resolve));
		if (props.state !== "loading") return;
	}
	animate.value = !reduced;
	shown.value = true;
	if (reduced) {
		progress.value = 1;
		return;
	}
	progress.value = Math.max(progress.value, 0.08);
	trickle();
}

function complete(): void {
	animate.value = !reducedMotion();
	progress.value = 1;
}

function hide(): void {
	if (!shown.value) return;
	// 200 ms para a barra chegar ao fim, 300 ms de fade, e só então volta a zero
	// — invisível, e sem transição, para a próxima começar da esquerda.
	hideTimer = setTimeout(() => {
		shown.value = false;
		resetTimer = setTimeout(() => {
			animate.value = false;
			progress.value = 0;
		}, 300);
	}, 200);
}

watch(
	() => props.state,
	(state) => {
		clearAll();
		if (state === "loading") void start();
		else if (state === "error") {
			tone.value = "danger";
			shown.value = true;
			complete();
		} else {
			complete();
			hide();
		}
	},
	{ immediate: true },
);

onBeforeUnmount(clearAll);
</script>

<template>
	<!-- z acima de tudo: header (h-14), doca do Ben (z-40/50) e toasts (z-60). -->
	<div
		aria-hidden="true"
		class="pointer-events-none fixed inset-x-0 top-0 z-[100] h-[3px] overflow-hidden"
		:data-state="state"
	>
		<div
			class="relative h-full w-full"
			:class="[
				tone === 'danger' ? 'bg-danger' : 'bg-accent',
				animate ? 'transition-[transform,opacity] duration-300 ease-out' : '',
			]"
			:style="{
				transform: `translate3d(${(progress - 1) * 100}%, 0, 0)`,
				opacity: shown ? 1 : 0,
			}"
			data-bar
		>
			<!-- O brilho na ponta, como o "peg" do nprogress: é o que faz uma linha de
			     3px ler como "está a andar" e não como uma borda do header. -->
			<div
				class="absolute top-0 right-0 h-full w-24 opacity-100 motion-reduce:hidden"
				:class="
					tone === 'danger'
						? 'shadow-[0_0_10px_var(--color-danger),0_0_5px_var(--color-danger)]'
						: 'shadow-[0_0_10px_var(--color-accent),0_0_5px_var(--color-accent)]'
				"
			/>
		</div>
	</div>
</template>
